// ─────────────────────────────────────────────────────────────────────────────
// Report Generator — Template-based auto monthly report generation
// Uses rule-based logic for recommendations, no external AI API
// Used by: smartCommandEngine.js → AIAssistantDrawer.jsx
// ─────────────────────────────────────────────────────────────────────────────

import { percentageChange, analyzeRevenueTrend } from '../utils/trendAnalytics';
import { getCurrencySymbol } from '../utils/formatters';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// null = the alert did not fire / the figure is not for this viewer.
const noAlert = (value) => (value == null ? 'No alert' : `${value}%`);
const money = (value) => (value == null ? '—' : `${getCurrencySymbol()}${value.toLocaleString('en-IN')}`);

// ── Recommendation engine (rule-based) ──────────────────────────────────────

const generateRecommendations = (metrics) => {
  const recommendations = [];

  // Revenue rules
  if (metrics.revenueChange < 0) {
    recommendations.push({
      type: 'warning',
      category: 'finance',
      message: `Revenue declined by ${Math.abs(metrics.revenueChange)}% compared to last month. Review pricing strategy and session utilization.`,
    });
  } else if (metrics.revenueChange > 20) {
    recommendations.push({
      type: 'success',
      category: 'finance',
      message: `Strong revenue growth of ${metrics.revenueChange}%. Consider investing in capacity expansion.`,
    });
  }

  // Cancellation rules
  if (metrics.cancellationRate > 8) {
    recommendations.push({
      type: 'warning',
      category: 'appointment',
      message: `Cancellation rate at ${metrics.cancellationRate}% — above 8% threshold. Review appointment confirmation process and send reminders.`,
    });
  }

  // Patient retention rules
  if (metrics.inactivePatientRate > 30) {
    recommendations.push({
      type: 'warning',
      category: 'patient',
      message: `${metrics.inactivePatientRate}% patients inactive for 30+ days. Initiate follow-up calls and re-engagement campaigns.`,
    });
  }

  // Expense ratio rules
  if (metrics.expenseRatio > 75) {
    recommendations.push({
      type: 'warning',
      category: 'finance',
      message: `Expense-to-revenue ratio at ${metrics.expenseRatio}% — approaching unsustainable levels. Review operational costs.`,
    });
  }

  // Overdue invoices
  if (metrics.overdueCount > 3) {
    recommendations.push({
      type: 'critical',
      category: 'finance',
      message: `${metrics.overdueCount} invoices overdue by 30+ days. Escalate collection efforts immediately.`,
    });
  }

  // Attendance rules
  if (metrics.staffAttendanceRate != null && metrics.staffAttendanceRate < 80) {
    recommendations.push({
      type: 'warning',
      category: 'analytics',
      message: `Staff attendance at ${metrics.staffAttendanceRate}%. Monitor absenteeism patterns and review scheduling.`,
    });
  }

  // Positive reinforcements
  if (metrics.newPatients > 5) {
    recommendations.push({
      type: 'success',
      category: 'patient',
      message: `${metrics.newPatients} new registrations this month — healthy growth trajectory.`,
    });
  }

  if (metrics.topTherapyGrowth > 10) {
    recommendations.push({
      type: 'success',
      category: 'appointment',
      message: `${metrics.topTherapy} demand increased by ${metrics.topTherapyGrowth}%. Consider adding more slots.`,
    });
  }

  // Fallback recommendation
  if (recommendations.length === 0) {
    recommendations.push({
      type: 'info',
      category: 'general',
      message: 'All metrics within normal range. Continue monitoring for changes.',
    });
  }

  return recommendations;
};

// ── Report generator ────────────────────────────────────────────────────────

/**
 * Generate a monthly report from dashboard/analytics data
 * @param {object} params - Report data inputs
 * @param {number[]} params.monthlyPayments - Revenue per month (Jan-Dec array)
 * @param {number[]} params.monthlyMembers - New members per month
 * @param {object} params.insights - Dashboard insights data
 * @param {object} params.invoiceCounts - Invoice summary counts
 * @param {object} params.overdueData - Overdue invoices data
 * @returns {object} Formatted report object
 */
export const generateMonthlyReport = ({
  monthlyPayments = [],
  monthlyMembers = [],
  insights = [],
  invoiceCounts = [],
  overdueData = {},
} = {}) => {
  const now = new Date();
  const currentMonthIndex = now.getMonth();
  const currentMonthName = MONTH_NAMES[currentMonthIndex];
  const currentYear = now.getFullYear();

  // Revenue analysis. monthlyPayments is null for a viewer who may not see the clinic's
  // revenue (smartCommandEngine canSeeRevenue): the report then leaves revenue out.
  const showRevenue = Array.isArray(monthlyPayments);
  const payments = showRevenue ? monthlyPayments : [];
  const currentRevenue = payments[currentMonthIndex] || 0;
  const prevRevenue = currentMonthIndex > 0 ? payments[currentMonthIndex - 1] : 0;
  const revenueChange = showRevenue ? percentageChange(currentRevenue, prevRevenue) : null;
  const revenueTrend = analyzeRevenueTrend(payments);

  // Members analysis
  const currentMembers = monthlyMembers[currentMonthIndex] || 0;
  const prevMembers = currentMonthIndex > 0 ? monthlyMembers[currentMonthIndex - 1] : 0;
  const memberChange = percentageChange(currentMembers, prevMembers);

  // Extract metrics from insights
  // The insights are alerts: each appears only when its figure crosses a line, so a missing
  // one is "no alert" (null), never a made-up 0%, 100% or "Speech Therapy".
  const cancellationInsight = insights.find(i => i.type === 'cancellation');
  const cancellationRate = cancellationInsight ? cancellationInsight.value : null;

  const inactiveInsight = insights.find(i => i.type === 'inactive_patients');
  const inactiveCount = inactiveInsight?.value || 0;

  const attendanceInsight = insights.find(i => i.type === 'attendance');
  const staffAttendanceRate = attendanceInsight ? attendanceInsight.value : null;

  const demandInsight = insights.find(i => i.type === 'demand');
  // analytics.service.getTherapyDemand: "<service> demand increased N% in <month>".
  const topTherapy = demandInsight ? String(demandInsight.message || '').split(' demand ')[0] || null : null;
  const topTherapyGrowth = demandInsight?.value || 0;

  const expenseInsight = insights.find(i => i.type === 'expense_ratio');
  const expenseRatio = expenseInsight ? expenseInsight.value : null;

  // Invoice metrics — the titles AdminUsersController.invoiceList sends. null = this
  // viewer may not see the clinic's totals, or the overdue list (view_finance).
  const totalPaid = invoiceCounts ? (invoiceCounts.find(c => c.title === 'Total Collected')?.value || 0) : null;
  const totalPending = invoiceCounts ? (invoiceCounts.find(c => c.title === 'Pending Payments')?.value || 0) : null;

  // Overdue
  const overdueCount = overdueData ? (overdueData.overdueCount || 0) : null;

  // Total patients (rough estimate)
  const totalPatients = monthlyMembers.reduce((a, b) => a + b, 0);
  const inactivePatientRate = totalPatients > 0 ? Math.round((inactiveCount / totalPatients) * 100) : 0;

  // Generate recommendations
  const recommendations = generateRecommendations({
    revenueChange,
    cancellationRate,
    inactivePatientRate,
    expenseRatio,
    overdueCount,
    staffAttendanceRate,
    newPatients: currentMembers,
    topTherapy,
    topTherapyGrowth,
  });

  // Build report sections — a section with nothing this viewer may see is left out.
  const showFinance = [totalPaid, totalPending, overdueCount, expenseRatio].some(v => v != null);
  const sections = [
    showRevenue && {
      title: 'Revenue Overview',
      icon: 'revenue',
      items: [
        { label: 'Current Month Revenue', value: `${getCurrencySymbol()}${currentRevenue.toLocaleString('en-IN')}` },
        { label: 'Month-over-Month Change', value: `${revenueChange > 0 ? '+' : ''}${revenueChange}%`, status: revenueChange >= 0 ? 'positive' : 'negative' },
        { label: 'Revenue Trend (3-month)', value: revenueTrend.trend, status: revenueTrend.trend === 'growing' ? 'positive' : revenueTrend.trend === 'declining' ? 'negative' : 'neutral' },
        { label: 'Total Revenue (YTD)', value: `${getCurrencySymbol()}${revenueTrend.totalRevenue?.toLocaleString('en-IN') || '0'}` },
        { label: 'Peak Month', value: `${revenueTrend.peakMonth?.month || 'N/A'} (${getCurrencySymbol()}${revenueTrend.peakMonth?.value?.toLocaleString('en-IN') || '0'})` },
      ],
    },
    {
      title: 'Patient Growth',
      icon: 'patient',
      items: [
        { label: 'New Registrations', value: `${currentMembers}` },
        { label: 'Growth Rate', value: `${memberChange > 0 ? '+' : ''}${memberChange}%`, status: memberChange >= 0 ? 'positive' : 'negative' },
        { label: 'Joined This Year', value: `${totalPatients}` },
        // analytics.service.getInactivePatients: established patients with no visit in 30+
        // days — not the patients marked Inactive, so it is named for what it counts.
        { label: 'No Visit in 30+ Days', value: inactiveInsight ? `${inactiveCount} (${inactivePatientRate}%)` : 'No alert', status: inactivePatientRate > 30 ? 'negative' : 'neutral' },
      ],
    },
    {
      title: 'Top Therapy',
      icon: 'therapy',
      items: [
        { label: 'Highest Demand', value: topTherapy || 'No jump in demand' },
        { label: 'Growth', value: topTherapy ? `+${topTherapyGrowth}%` : '—', status: topTherapy ? 'positive' : 'neutral' },
        { label: 'Cancellation Rate', value: noAlert(cancellationRate), status: cancellationRate > 8 ? 'negative' : 'neutral' },
      ],
    },
    showFinance && {
      title: 'Financial Health',
      icon: 'finance',
      items: [
        { label: 'Total Collected', value: money(totalPaid) },
        { label: 'Pending Payments', value: money(totalPending), status: totalPending == null ? 'neutral' : totalPending > 0 ? 'negative' : 'positive' },
        { label: 'Overdue Invoices', value: overdueCount == null ? '—' : `${overdueCount}`, status: overdueCount > 3 ? 'negative' : 'neutral' },
        { label: 'Expense Ratio', value: expenseRatio == null ? '—' : `${expenseRatio}%`, status: expenseRatio > 75 ? 'negative' : 'neutral' },
      ],
    },
    {
      title: 'Staff & Operations',
      icon: 'staff',
      items: [
        { label: 'Staff Attendance', value: noAlert(staffAttendanceRate), status: staffAttendanceRate == null ? 'neutral' : staffAttendanceRate < 80 ? 'negative' : 'positive' },
      ],
    },
  ].filter(Boolean);

  // Build text summary
  const textSummary = buildTextReport({
    currentMonthName,
    currentYear,
    currentRevenue,
    revenueChange,
    revenueTrend,
    currentMembers,
    memberChange,
    topTherapy,
    topTherapyGrowth,
    cancellationRate,
    overdueCount,
    expenseRatio,
    staffAttendanceRate,
    recommendations,
  });

  return {
    title: `Monthly Report — ${currentMonthName} ${currentYear}`,
    generatedAt: now.toISOString(),
    period: { month: currentMonthName, year: currentYear },
    sections,
    recommendations,
    textSummary,
    confidence: revenueTrend.confidence_score || 50,
  };
};

// ── Text report builder ─────────────────────────────────────────────────────

const buildTextReport = ({
  currentMonthName,
  currentYear,
  currentRevenue,
  revenueChange,
  revenueTrend,
  currentMembers,
  memberChange,
  topTherapy,
  topTherapyGrowth,
  cancellationRate,
  overdueCount,
  expenseRatio,
  staffAttendanceRate,
  recommendations,
}) => {
  const lines = [];

  lines.push(`MONTHLY REPORT — ${currentMonthName.toUpperCase()} ${currentYear}`);
  lines.push('═'.repeat(45));
  lines.push('');

  // Revenue (null change = not for this viewer)
  if (revenueChange != null) {
    lines.push('▸ REVENUE');
    lines.push(`  Current: ${getCurrencySymbol()}${currentRevenue.toLocaleString('en-IN')}`);
    lines.push(`  Change: ${revenueChange > 0 ? '+' : ''}${revenueChange}% MoM`);
    lines.push(`  Trend: ${revenueTrend.trend} (confidence: ${revenueTrend.confidence_score}%)`);
    lines.push('');
  }

  // Patients
  lines.push('▸ PATIENTS');
  lines.push(`  New Registrations: ${currentMembers}`);
  lines.push(`  Growth: ${memberChange > 0 ? '+' : ''}${memberChange}%`);
  lines.push('');

  // Therapy
  lines.push('▸ TOP THERAPY');
  lines.push(topTherapy ? `  ${topTherapy}: +${topTherapyGrowth}% demand` : '  No therapy\'s demand jumped last month');
  lines.push(`  Cancellation Rate: ${noAlert(cancellationRate)}`);
  lines.push('');

  // Operations
  lines.push('▸ OPERATIONS');
  lines.push(`  Staff Attendance: ${noAlert(staffAttendanceRate)}`);
  lines.push(`  Overdue Invoices: ${overdueCount == null ? '—' : overdueCount}`);
  lines.push(`  Expense Ratio: ${expenseRatio == null ? '—' : `${expenseRatio}%`}`);
  lines.push('');

  // Recommendations
  lines.push('▸ RECOMMENDATIONS');
  recommendations.forEach((rec, i) => {
    const icon = rec.type === 'warning' ? '⚠' : rec.type === 'critical' ? '✖' : rec.type === 'success' ? '✓' : 'ℹ';
    lines.push(`  ${icon} ${rec.message}`);
  });

  return lines.join('\n');
};

export default generateMonthlyReport;
